import {
  Badge,
  Button,
  Group,
  Stack,
  Title,
  TextInput,
  ActionIcon,
  Tooltip,
  NumberInput,
  Modal,
} from "@mantine/core";
import { IconEdit, IconCheck, IconX, IconPlus } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useForm } from "@mantine/form";

export default function EditablePriorityArray({
  value,
  fieldName,
  editingField,
  setEditingField,
  onSave,
  title,
  renderBadge,
  modalTitle,
  nameLabel = "Name",
  priorityLabel = "Priority",
  websiteLabel = "Website URL",
  disabled = false,
}) {
  const [modalOpened, setModalOpened] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [tempArray, setTempArray] = useState(value || []);

  const isEditing = editingField === fieldName;

  const form = useForm({
    initialValues: {
      name: "",
      priority: 1,
      website: "",
    },
  });

  useEffect(() => {
    if (isEditing) {
      setTempArray(value || []);
    }
  }, [isEditing, value]);

  const handleAddItem = (values) => {
    const newArray = [...tempArray, values];
    setTempArray(newArray);
    form.reset();
    setModalOpened(false);
  };

  const handleEditItem = (index) => {
    const item = tempArray[index];
    form.setValues(item);
    setEditingIndex(index);
    setModalOpened(true);
  };

  const handleUpdateItem = (values) => {
    const newArray = [...tempArray];
    newArray[editingIndex] = values;
    setTempArray(newArray);
    form.reset();
    setEditingIndex(null);
    setModalOpened(false);
  };

  const handleRemoveItem = (index) => {
    const newArray = tempArray.filter((_, i) => i !== index);
    setTempArray(newArray);
  };

  const handleSave = () => {
    onSave(fieldName, tempArray);
    setEditingField(null);
  };

  const handleCancel = () => {
    setEditingField(null);
    setTempArray(value || []);
  };

  if (isEditing) {
    return (
      <Stack gap="sm">
        <Group justify="space-between">
          <Title order={3}>{title}</Title>
          <Group gap="xs">
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconPlus size={14} />}
              onClick={() => {
                form.reset();
                setEditingIndex(null);
                setModalOpened(true);
              }}
            >
              Add
            </Button>
            <ActionIcon color="green" onClick={handleSave}>
              <IconCheck size={16} />
            </ActionIcon>
            <ActionIcon color="red" onClick={handleCancel}>
              <IconX size={16} />
            </ActionIcon>
          </Group>
        </Group>
        <Group gap="xs">
          {tempArray.map((item, index) => (
            <Badge
              key={index}
              variant="light"
              size="sm"
              style={{ cursor: "pointer" }}
              leftSection={item.priority?.toFixed(1)}
              rightSection={
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="red"
                  onClick={(evt) => {
                    handleRemoveItem(index);
                    evt.stopPropagation();
                  }}
                >
                  <IconX size={10} />
                </ActionIcon>
              }
              onClick={() => handleEditItem(index)}
            >
              {renderBadge ? renderBadge(item) : item.name}
            </Badge>
          ))}
        </Group>
        <Modal
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
          title={modalTitle}
        >
          <form
            onSubmit={form.onSubmit(
              editingIndex !== null ? handleUpdateItem : handleAddItem
            )}
          >
            <Stack gap="md">
              <TextInput
                label={nameLabel}
                {...form.getInputProps("name")}
                required
              />
              <TextInput
                label={websiteLabel}
                {...form.getInputProps("website")}
                placeholder="https://example.com"
              />
              <NumberInput
                label={priorityLabel}
                {...form.getInputProps("priority")}
                min={0}
                max={10}
                step={0.1}
                required
              />
              <Group justify="flex-end">
                <Button variant="subtle" onClick={() => setModalOpened(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingIndex !== null ? "Update" : "Add"}
                </Button>
              </Group>
            </Stack>
          </form>
        </Modal>
      </Stack>
    );
  }

  return (
    <Group gap="xs">
      <Title order={3}>{title}</Title>
      {!disabled && (
        <Tooltip label="Click to edit">
          <ActionIcon
            size="sm"
            variant="subtle"
            onClick={() => setEditingField(fieldName)}
          >
            <IconEdit size={14} />
          </ActionIcon>
        </Tooltip>
      )}
      <Group gap="xs">
        {value?.map((item, index) => (
          <Badge
            key={index}
            variant="light"
            size="sm"
            leftSection={item.priority?.toFixed(1)}
          >
            {renderBadge ? renderBadge(item) : item.website ? (
              <a
                href={item.website}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                {item.name}
              </a>
            ) : (
              item.name
            )}
          </Badge>
        ))}
      </Group>
    </Group>
  );
}
